import { useState } from "react";
import { fmtTime, fmtAge, priorityInfo } from "@/lib/format";
import { useNow } from "@/hooks/useNow";

export function PriorityChip({ priority, compact = false }) {
  const p = priorityInfo(priority);
  return (
    <span data-testid="priority-chip" className={`chip mono ${p.cls}`} title={p.label}>
      <span className={`h-1.5 w-1.5 rounded-full ${p.dot} ${p.pulse ? "pulse-soft" : ""}`} />
      {compact ? p.short : p.label}
    </span>
  );
}

export function useDropUnit(onDropUnit, incidentId) {
  const [over, setOver] = useState(false);
  return {
    over,
    handlers: {
      onDragOver: (e) => { if (e.dataTransfer.types.includes("text/unit-id")) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setOver(true); } },
      onDragLeave: () => setOver(false),
      onDrop: (e) => { e.preventDefault(); setOver(false); const uid = e.dataTransfer.getData("text/unit-id"); if (uid) onDropUnit(incidentId, uid); },
    },
  };
}

function PendingRow({ inc, selected, onSelect, onDropUnit, now }) {
  const { over, handlers } = useDropUnit(onDropUnit, inc.id);
  return (
    <tr data-testid={`pending-row-${inc.incident_number}`} onClick={() => onSelect(inc.id)} {...handlers}
      className={`row-hover cursor-pointer border-b border-border/60 ${selected ? "row-selected" : ""} ${over ? "drop-target" : ""}`}>
      <td className="px-2 py-1"><PriorityChip priority={inc.priority} /></td>
      <td className="px-2 py-1 mono text-xs font-semibold whitespace-nowrap">{inc.incident_number}</td>
      <td className="px-2 py-1 mono text-xs whitespace-nowrap">{fmtTime(inc.received_at, false)} <span className="text-muted-foreground">· {fmtAge(inc.received_at, now)}</span></td>
      <td className="px-2 py-1 text-xs font-medium whitespace-nowrap">{inc.call_type_name || <span className="text-muted-foreground">No call type</span>}</td>
      <td className="px-2 py-1 text-xs truncate max-w-[260px]" title={inc.location_raw}>{inc.location_raw || <span className="text-muted-foreground italic">Location pending</span>}</td>
    </tr>
  );
}

export function PendingQueue({ items, selectedId, onSelect, onDropUnit }) {
  const now = useNow(1000);
  return (
    <section className="pane h-full" data-testid="pending-pane">
      <div className="pane-head">
        <span className="overline-label">Pending Calls</span>
        <span className="mono text-xs text-muted-foreground" data-testid="pending-count">{items.length} waiting · sorted by priority, then received</span>
      </div>
      <div className="flex-1 min-h-0 overflow-auto">
        {items.length === 0 ? (
          <div className="p-4 text-xs text-muted-foreground" data-testid="pending-empty">No pending calls. Drop a unit on a call to assign it.</div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead className="sticky top-0 bg-card z-10">
              <tr className="overline-label border-b">
                <th className="px-2 py-1 font-medium">Priority</th><th className="px-2 py-1 font-medium">Incident</th><th className="px-2 py-1 font-medium">Received · Age</th><th className="px-2 py-1 font-medium">Call Type</th><th className="px-2 py-1 font-medium">Location</th>
              </tr>
            </thead>
            <tbody>
              {items.map((inc) => <PendingRow key={inc.id} inc={inc} selected={inc.id === selectedId} onSelect={onSelect} onDropUnit={onDropUnit} now={now} />)}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
